import React from "react";

// Faithful port of the new-UI custom icon set.
// Stroke 1.6, rounded joins, viewBox 0 0 24 24.

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const props = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    style: { flexShrink: 0, display: "block" },
  };
  switch (name) {
    case "box":
      return (
        <svg {...props}>
          <path d="M3.5 7.5L12 3l8.5 4.5L12 12 3.5 7.5z" />
          <path d="M3.5 7.5v9L12 21l8.5-4.5v-9" />
          <path d="M12 12v9" />
        </svg>
      );
    // --- variant-workbench tab set. Deliberately four different metaphors so
    // no two tabs share a silhouette (the old "box" and "vendors" glyphs were
    // both the same 3D cube). ---
    case "stock":
      // Boxes stacked on a shelf — quantity on hand.
      return (
        <svg {...props}>
          <rect x="2.75" y="10" width="7.5" height="5.5" rx="1" />
          <rect x="13.75" y="10" width="7.5" height="5.5" rx="1" />
          <rect x="8.25" y="3.75" width="7.5" height="5.5" rx="1" />
          <path d="M2.5 19h19" />
        </svg>
      );
    case "sliders":
      // Two toggles — per-variant settings.
      return (
        <svg {...props}>
          <path d="M3.5 8h9M17.5 8h3" />
          <circle cx="15" cy="8" r="2.2" />
          <path d="M3.5 16h3M11.5 16h9" />
          <circle cx="9" cy="16" r="2.2" />
        </svg>
      );
    case "supplier":
      // People you buy from — distinct from the delivery truck and shopfront.
      return (
        <svg {...props}>
          <circle cx="9" cy="8" r="3.2" />
          <path d="M2.9 19.3a6.2 6.2 0 0 1 12.2 0" />
          <path d="M16.6 5.4a3.2 3.2 0 0 1 0 6" />
          <path d="M18.1 13.6a6.2 6.2 0 0 1 3 5.7" />
        </svg>
      );
    case "barcode":
      // The codes themselves, not a price tag.
      return (
        <svg {...props}>
          <path d="M4 6v12M7.5 6v12M11 6v8M14.5 6v12M18 6v8M20.5 6v12" />
        </svg>
      );
    case "search":
      return (
        <svg {...props}>
          <circle cx="11" cy="11" r="6.5" />
          <path d="M16 16l4 4" />
        </svg>
      );
    case "more":
      return (
        <svg {...props}>
          <circle cx="12" cy="5.5" r="1.4" fill={color} stroke="none" />
          <circle cx="12" cy="12" r="1.4" fill={color} stroke="none" />
          <circle cx="12" cy="18.5" r="1.4" fill={color} stroke="none" />
        </svg>
      );
    case "eye":
      return (
        <svg {...props}>
          <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case "list":
      return (
        <svg {...props}>
          <path d="M9 6h11M9 12h11M9 18h11" />
          <circle cx="4.5" cy="6" r="0.9" fill={color} stroke="none" />
          <circle cx="4.5" cy="12" r="0.9" fill={color} stroke="none" />
          <circle cx="4.5" cy="18" r="0.9" fill={color} stroke="none" />
        </svg>
      );
    case "plus":
      return (
        <svg {...props}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case "sort":
      return (
        <svg {...props}>
          <path d="M7 4v16M3 16l4 4 4-4" />
          <path d="M17 20V4M13 8l4-4 4 4" />
        </svg>
      );
    case "check":
      return (
        <svg {...props}>
          <path d="M5 12l4 4 10-10" />
        </svg>
      );
    case "chevron-down":
      return (
        <svg {...props}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    case "chevron-right":
      return (
        <svg {...props}>
          <path d="M9 6l6 6-6 6" />
        </svg>
      );
    case "chevron-left":
      return (
        <svg {...props}>
          <path d="M15 6l-6 6 6 6" />
        </svg>
      );
    case "calendar":
      return (
        <svg {...props}>
          <rect x="4" y="5" width="16" height="15" rx="2" />
          <path d="M8 3v4M16 3v4M4 10h16" />
        </svg>
      );
    case "upload":
      return (
        <svg {...props}>
          <path d="M12 16V6" />
          <path d="M8 10l4-4 4 4" />
          <path d="M4 20h16" />
        </svg>
      );
    case "image":
      return (
        <svg {...props}>
          <rect x="4" y="5" width="16" height="14" rx="2" />
          <circle cx="9" cy="10" r="1.5" />
          <path d="M4 17l4-4 4 4 4-5 4 5" />
        </svg>
      );
    case "edit":
      return (
        <svg {...props}>
          <path d="M4 20h4l10-10-4-4L4 16v4z" />
          <path d="M14 6l4 4" />
        </svg>
      );
    case "trash":
      return (
        <svg {...props}>
          <path d="M4 7h16" />
          <path d="M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2" />
          <path d="M6 7l1 12.5a1.5 1.5 0 001.5 1.4h7a1.5 1.5 0 001.5-1.4L18 7" />
        </svg>
      );
    case "tag":
      return (
        <svg {...props}>
          <path d="M3 12V4a1 1 0 011-1h8l9 9-9 9-9-9z" />
          <circle cx="8" cy="8" r="1.5" />
        </svg>
      );
    case "category":
      return (
        <svg {...props}>
          <rect x="3.5" y="3.5" width="7" height="7" rx="1.2" />
          <rect x="13.5" y="3.5" width="7" height="7" rx="1.2" />
          <rect x="3.5" y="13.5" width="7" height="7" rx="1.2" />
          <rect x="13.5" y="13.5" width="7" height="7" rx="1.2" />
        </svg>
      );
    case "brand":
      return (
        <svg {...props}>
          <path d="M4 7l8-4 8 4v3a8 8 0 11-16 0V7z" />
          <path d="M9 11l2 2 4-4" />
        </svg>
      );
    case "attributes":
      return (
        <svg {...props}>
          <path d="M4 7h10" />
          <circle cx="17" cy="7" r="2.2" />
          <path d="M20 13H10" />
          <circle cx="7" cy="13" r="2.2" />
          <path d="M4 19h10" />
          <circle cx="17" cy="19" r="2.2" />
        </svg>
      );
    case "close":
      return (
        <svg {...props}>
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      );
    case "cart":
      return (
        <svg {...props}>
          <path d="M3 4h2.5l1.5 11h11l2-8H6.5" />
          <circle cx="9" cy="19" r="1.4" />
          <circle cx="17" cy="19" r="1.4" />
        </svg>
      );
    case "medal":
      return (
        <svg {...props}>
          <circle cx="12" cy="15" r="5" />
          <path d="M9 11.5L7 4l4 1 1-2 1 2 4-1-2 7.5" />
          <path d="M11 14l1 1 2-2" />
        </svg>
      );
    case "star":
      return (
        <svg {...props}>
          <path d="M12 3l2.7 5.5 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.8 1-6.1L3.2 9.4l6.1-.9L12 3z" />
        </svg>
      );
    case "info":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5M12 8.5v.01" />
        </svg>
      );
    case "grip":
      return (
        <svg {...props}>
          <circle cx="9" cy="6" r="1.2" />
          <circle cx="15" cy="6" r="1.2" />
          <circle cx="9" cy="12" r="1.2" />
          <circle cx="15" cy="12" r="1.2" />
          <circle cx="9" cy="18" r="1.2" />
          <circle cx="15" cy="18" r="1.2" />
        </svg>
      );
    case "shop":
      return (
        <svg {...props}>
          <path d="M3 9l1.5-5h15L21 9" />
          <path d="M4 9v10a1 1 0 001 1h14a1 1 0 001-1V9" />
          <path d="M9 20v-6h6v6" />
          <path d="M3 9a3 3 0 006 0 3 3 0 006 0 3 3 0 006 0" />
        </svg>
      );
    case "truck":
      return (
        <svg {...props}>
          <rect x="2" y="7" width="12" height="9" rx="1" />
          <path d="M14 10h4l3 3v3h-7" />
          <circle cx="7" cy="18" r="1.8" />
          <circle cx="17" cy="18" r="1.8" />
        </svg>
      );
    case "gift":
      return (
        <svg {...props}>
          <rect x="3" y="9" width="18" height="12" rx="1.2" />
          <path d="M3 13h18" />
          <path d="M12 9v12" />
          <path d="M8 9a2.5 2.5 0 010-5c2 0 4 5 4 5s-2 0-4 0z" />
          <path d="M16 9a2.5 2.5 0 000-5c-2 0-4 5-4 5s2 0 4 0z" />
        </svg>
      );
    case "eye-off":
      return (
        <svg {...props}>
          <path d="M3 3l18 18" />
          <path d="M10.6 10.6a2 2 0 002.8 2.8" />
          <path d="M9.5 5.4A10 10 0 0112 5c5 0 9 4.5 10 7-.4 1-1.3 2.4-2.7 3.7" />
          <path d="M6.7 6.7C4.4 8.2 2.7 10.5 2 12c1 2.5 5 7 10 7 1.7 0 3.2-.5 4.5-1.2" />
        </svg>
      );
    case "register":
      return (
        <svg {...props}>
          <rect x="3" y="9" width="18" height="11" rx="1.5" />
          <path d="M6 9V5a1 1 0 011-1h10a1 1 0 011 1v4" />
          <path d="M7 13h3M13 13h4M7 16.5h10" />
        </svg>
      );
    case "download":
      return (
        <svg {...props}>
          <path d="M12 4v12" />
          <path d="M7 11l5 5 5-5" />
          <path d="M4 20h16" />
        </svg>
      );
    case "print":
      return (
        <svg {...props}>
          <path d="M6 9V3h12v6" />
          <rect x="6" y="14" width="12" height="7" rx="1" />
          <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
          <circle cx="17" cy="11" r="1" />
        </svg>
      );
    case "archive":
      return (
        <svg {...props}>
          <rect x="3" y="4" width="18" height="4" rx="1" />
          <path d="M5 8v11a1 1 0 001 1h12a1 1 0 001-1V8" />
          <path d="M10 12h4" />
        </svg>
      );
    case "clipboard-check":
      return (
        <svg {...props}>
          <rect x="6" y="4" width="12" height="16" rx="2" />
          <path d="M9 4V3a1 1 0 011-1h4a1 1 0 011 1v1" />
          <path d="M9 13l2 2 4-4" />
        </svg>
      );
    case "transfers":
      return (
        <svg {...props}>
          <path d="M4 8h13" />
          <path d="M14 5l3 3-3 3" />
          <path d="M20 16H7" />
          <path d="M10 13l-3 3 3 3" />
        </svg>
      );
    case "orders":
      return (
        <svg {...props}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <path d="M8 9h8M8 13h8M8 17h5" />
        </svg>
      );
    case "vendors":
      return (
        <svg {...props}>
          <path d="M3 8l9-5 9 5v8l-9 5-9-5V8z" />
          <path d="M3 8l9 5 9-5" />
          <path d="M12 13v8" />
        </svg>
      );
    case "refresh":
      return (
        <svg {...props}>
          <path d="M20 12a8 8 0 11-3.2-6.4" />
          <path d="M20 4v5h-5" />
        </svg>
      );
    case "inventory":
      return (
        <svg {...props}>
          <rect x="3" y="4" width="18" height="6" rx="1.5" />
          <path d="M5 10v9.5a.5.5 0 00.5.5h13a.5.5 0 00.5-.5V10" />
          <path d="M10 14h4" />
        </svg>
      );
    case "bell":
      return (
        <svg {...props}>
          <path d="M6 9a6 6 0 1112 0v4l1.5 3h-15L6 13V9z" />
          <path d="M10 19a2 2 0 004 0" />
        </svg>
      );
    case "list":
      return (
        <svg {...props}>
          <path d="M3 7h2M3 12h2M3 17h2" />
          <rect x="7.5" y="5" width="13" height="4" rx="1" />
          <rect x="7.5" y="10" width="13" height="4" rx="1" />
          <rect x="7.5" y="15" width="13" height="4" rx="1" />
        </svg>
      );
    case "filter":
      return (
        <svg {...props}>
          <path d="M4 6h16M7 12h10M10 18h4" />
        </svg>
      );
    case "grid":
      return (
        <svg {...props}>
          <rect x="3.5" y="3.5" width="7" height="7" rx="1.2" />
          <rect x="13.5" y="3.5" width="7" height="7" rx="1.2" />
          <rect x="3.5" y="13.5" width="7" height="7" rx="1.2" />
          <rect x="13.5" y="13.5" width="7" height="7" rx="1.2" />
        </svg>
      );
    case "grid-plus":
      return (
        <svg {...props}>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <path d="M17.5 14v7M14 17.5h7" />
        </svg>
      );
    case "barcode":
      return (
        <svg {...props}>
          <path d="M4 6v12M7 6v12M10 6v12M13 6v12M16 6v12M19 6v12" strokeWidth="1.4" />
        </svg>
      );
    case "sliders":
      return (
        <svg {...props}>
          <path d="M5 6h8M17 6h2" />
          <circle cx="15" cy="6" r="2" />
          <path d="M5 12h2M11 12h8" />
          <circle cx="9" cy="12" r="2" />
          <path d="M5 18h10M19 18h0" />
          <circle cx="17" cy="18" r="2" />
        </svg>
      );
    case "arrow-down":
      return (
        <svg {...props}>
          <path d="M12 5v14M5 12l7 7 7-7" />
        </svg>
      );
    case "box-open":
      return (
        <svg {...props}>
          <path d="M3 8l9-4 9 4v9l-9 4-9-4V8z" />
          <path d="M3 8l9 4 9-4M12 12v9" />
          <path d="M9 6.2l6 2.6" />
        </svg>
      );
    default:
      return null;
  }
};

export default Icon;
